import type { JSONContent } from "@tiptap/react";

export type StarterTemplate = {
	id: string;
	name: string;
	description: string;
	subject: string;
	content: JSONContent;
};

// Helper to create paragraph node
function p(text: string): JSONContent {
	return {
		type: "paragraph",
		content: text ? [{ type: "text", text }] : undefined,
	};
}

// Helper to create heading node
function h(level: 1 | 2 | 3, text: string): JSONContent {
	return {
		type: "heading",
		attrs: { level },
		content: [{ type: "text", text }],
	};
}

// Helper to create button node
function button(
	text: string,
	href: string,
	align: "left" | "center" | "right" = "left",
): JSONContent {
	return {
		type: "emailButton",
		attrs: {
			href,
			color: "#4f46e5",
			textColor: "#ffffff",
			align,
		},
		content: [{ type: "text", text }],
	};
}

// Helper to create spacer node
function spacer(height: number): JSONContent {
	return {
		type: "spacer",
		attrs: { height },
	};
}

// Helper to create divider node
function divider(): JSONContent {
	return {
		type: "emailDivider",
		attrs: { color: "#e5e7eb", margin: 24 },
	};
}

export const STARTER_TEMPLATES: StarterTemplate[] = [
	{
		id: "blank",
		name: "Blank",
		description: "Start from scratch",
		subject: "",
		content: {
			type: "doc",
			content: [p("")],
		},
	},
	{
		id: "welcome",
		name: "Welcome",
		description: "Onboard new users",
		subject: "Welcome to {{company}}!",
		content: {
			type: "doc",
			content: [
				h(1, "Welcome aboard, {{name}}!"),
				p(
					"We're thrilled to have you join us. Your account is all set up and ready to go.",
				),
				p("Here are a few things you can do to get started:"),
				p(
					"• Complete your profile\n• Explore our features\n• Check out our documentation",
				),
				button("Go to Dashboard", "{{dashboard_url}}"),
				spacer(24),
				divider(),
				p("Questions? Reply to this email or visit our help center."),
			],
		},
	},
	{
		id: "password-reset",
		name: "Password Reset",
		description: "Reset password link",
		subject: "Reset your password",
		content: {
			type: "doc",
			content: [
				h(1, "Reset your password"),
				p("Hi {{name}},"),
				p(
					"We received a request to reset your password. Click the button below to choose a new password:",
				),
				button("Reset Password", "{{reset_url}}", "center"),
				spacer(16),
				{
					type: "paragraph",
					attrs: { textAlign: "center" },
					content: [{ type: "text", text: "This link expires in 1 hour." }],
				},
				divider(),
				p(
					"If you didn't request this, you can safely ignore this email. Your password won't be changed.",
				),
			],
		},
	},
	{
		id: "verification",
		name: "Email Verification",
		description: "Verify email address",
		subject: "Verify your email address",
		content: {
			type: "doc",
			content: [
				h(1, "Verify your email"),
				p("Hi {{name}},"),
				p("Please verify your email address by clicking the button below:"),
				button("Verify Email", "{{verify_url}}", "center"),
				spacer(16),
				{
					type: "paragraph",
					attrs: { textAlign: "center" },
					content: [{ type: "text", text: "This link expires in 24 hours." }],
				},
				divider(),
				p("If you didn't create an account, you can safely ignore this email."),
			],
		},
	},
	{
		id: "invoice",
		name: "Invoice / Receipt",
		description: "Payment confirmation",
		subject: "Your receipt from {{company}}",
		content: {
			type: "doc",
			content: [
				h(1, "Thanks for your order!"),
				p("Hi {{name}}, here's your receipt:"),
				divider(),
				h(3, "Order #{{order_id}}"),
				p("{{order_items}}"),
				h(3, "Total: {{total}}"),
				divider(),
				button("View Order Details", "{{order_url}}"),
				spacer(24),
				p("Thank you for your business!"),
			],
		},
	},
	{
		id: "invitation",
		name: "Team Invitation",
		description: "Invite to team/workspace",
		subject: "{{inviter}} invited you to join {{team}}",
		content: {
			type: "doc",
			content: [
				h(1, "You're invited!"),
				p("Hi there,"),
				p("{{inviter}} has invited you to join {{team}} on {{company}}."),
				button("Accept Invitation", "{{invite_url}}", "center"),
				spacer(16),
				{
					type: "paragraph",
					attrs: { textAlign: "center" },
					content: [
						{ type: "text", text: "This invitation expires in 7 days." },
					],
				},
				divider(),
				p(
					"If you don't recognize this sender, you can safely ignore this email.",
				),
			],
		},
	},
];
